import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { X } from "lucide-react";
import { useDirtyGuard } from "../../context/DirtyGuard";
import { NeuCard } from "../../components/neu/Neu";
import { leaveBalance, requestLeave } from "../../api/payroll";
import { useApiError } from "../../hooks/useApiError";
import { LeavesBalanceGrid } from "./LeavesBalanceGrid";
import { LeaveFormFields } from "./LeaveFormFields";
import { TimeOffForm } from "./TimeOffForm";
import { TimeOffList } from "./TimeOffList";

const EMPTY = { start_date: "", end_date: "", leave_type: "", reason: "", certificate_url: "" };

export const LeavesTabContent = ({ timeOff, onDone, reloadToken }) => {
  const { t } = useTranslation();
  const describe = useApiError();
  const [balance, setBalance] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const formRef = useRef(null);

  const load = async () => setBalance(await leaveBalance());
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (form.leave_type && formRef.current) {
      formRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [form.leave_type]);

  const send = useCallback(async () => {
    const res = await requestLeave({ ...form, end_date: form.end_date || form.start_date });
    toast.success(t("leaves.submitted", { count: res.total_days }));
  }, [form, t]);
  useDirtyGuard(Boolean(form.start_date || form.end_date || form.reason), send);

  if (!balance) return null;
  const kind = form.leave_type;
  const annualLocked = kind === "annual" && balance.annual_locked;
  const isTimeOff = kind === "time_off";
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await send(); setForm(EMPTY); load(); onDone?.(); }
    catch (error) { toast.error(describe(error)); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid gap-5" data-testid="leaves-tab">
      <LeavesBalanceGrid balance={balance} timeOff={timeOff} value={kind}
                         onSelect={(k) => setForm({ ...EMPTY, leave_type: k })} />
      {kind ? (
        <div ref={formRef} className="scroll-mt-4">
          <NeuCard>
            <div className="flex items-center justify-between">
              <p className="label-xs">{t(`leaves.types.${kind}`)}</p>
              <button type="button" onClick={() => setForm(EMPTY)} data-testid="leave-cancel"
                      aria-label="close"
                      className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-background text-muted-foreground shadow-neu-sm hover:text-destructive">
                <X className="h-4 w-4" />
              </button>
            </div>
            {annualLocked ? (
              <p className="mt-4 rounded-2xl bg-background p-4 text-center text-sm font-black text-destructive shadow-neu-in"
                 data-testid="annual-locked-hint">
                {t("leaves.annualLockedHint", { date: balance.eligible_from, days: balance.annual_days_left })}
              </p>
            ) : isTimeOff ? (
              <div className="mt-4 grid gap-4">
                <TimeOffForm balance={timeOff} onDone={onDone} />
                <TimeOffList reloadToken={reloadToken} />
              </div>
            ) : (
              <LeaveFormFields balance={balance} form={form} setForm={setForm} onSubmit={submit} busy={busy} />
            )}
          </NeuCard>
        </div>
      ) : null}
    </div>
  );
};
